import { cn } from '@/utils/cn';
import { STORY_PLACEHOLDER } from '@/constants/artistStory';
import { SparkleMaskedName } from '@/components/ui/SparkleMaskedName';
import React from 'react';

/** Lets long unbroken strings wrap inside flex/grid parents (needs min-w-0 + self-stretch with items-center). */
const WORD_WRAP_ROOT =
  'w-full min-w-0 max-w-full self-stretch [overflow-wrap:anywhere] break-words [&_*]:min-w-0';

type StoryVariant = 'free' | 'split';

const variantClasses: Record<StoryVariant, string> = {
  free: cn(
    'max-w-[1249px] text-[14px] sm:text-[20px] opacity-80',
    '[&_h1]:text-xl [&_h1]:sm:text-2xl [&_h1]:font-bold [&_h1]:mb-2 [&_h1]:mt-4 [&_h1]:first:mt-0',
    '[&_h2]:text-lg [&_h2]:sm:text-xl [&_h2]:font-bold [&_h2]:mb-2 [&_h2]:mt-3 [&_h2]:first:mt-0',
    '[&_h3]:text-base [&_h3]:sm:text-lg [&_h3]:font-semibold [&_h3]:mb-1.5 [&_h3]:mt-2 [&_h3]:first:mt-0',
    '[&_p]:mb-3 [&_p]:last:mb-0 [&_p:empty]:hidden',
    '[&_strong]:font-semibold [&_b]:font-semibold',
    '[&_em]:italic [&_i]:italic',
    '[&_u]:underline',
    '[&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-3',
    '[&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-3',
    '[&_li]:mb-1',
    '[&_a]:underline [&_a]:text-[#F33363] [&_a:hover]:text-[#F33363]',
  ),
  split: cn(
    'text-xl max-sm:text-sm max-w-80',
    '[&_h1]:text-xl [&_h1]:max-sm:text-lg [&_h1]:font-bold [&_h1]:mb-2 [&_h1]:mt-3 [&_h1]:first:mt-0',
    '[&_h2]:text-lg [&_h2]:max-sm:text-base [&_h2]:font-bold [&_h2]:mb-2 [&_h2]:mt-2.5 [&_h2]:first:mt-0',
    '[&_h3]:text-base [&_h3]:max-sm:text-sm [&_h3]:font-semibold [&_h3]:mb-1 [&_h3]:mt-2 [&_h3]:first:mt-0',
    '[&_p]:mb-3 [&_p]:last:mb-0 [&_p:empty]:hidden',
    '[&_strong]:font-semibold [&_b]:font-semibold',
    '[&_em]:italic [&_i]:italic',
    '[&_u]:underline',
    '[&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-3',
    '[&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-3',
    '[&_li]:mb-1',
    '[&_a]:underline [&_a]:text-[#F33363] [&_a:hover]:text-[#F33363]',
  ),
};

type ArtistStoryRichTextProps = {
  storyHtml: string | null;
  hasStory: boolean;
  variant: StoryVariant;
  className?: string;
  placeholder?: string;
  maskExactName?: string;
};

function htmlToReactNodesWithMaskedName(html: string, maskExactName: string): React.ReactNode {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const normalizedName = maskExactName.trim();
  const escapedName = normalizedName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const namePattern = escapedName ? new RegExp(`(${escapedName})`, 'gi') : null;

  const toReactNode = (node: ChildNode, key: string): React.ReactNode => {
    if (node.nodeType === Node.TEXT_NODE) {
      const rawText = node.textContent ?? '';
      if (!namePattern || normalizedName.length === 0 || rawText.length === 0) {
        return rawText;
      }

      const parts = rawText.split(namePattern);
      if (parts.length <= 1) return rawText;

      return parts.map((part, idx) => {
        if (part.toLowerCase() === normalizedName.toLowerCase()) {
          return <SparkleMaskedName key={`${key}-mask-${idx}`} name={maskExactName} />;
        }
        return <React.Fragment key={`${key}-text-${idx}`}>{part}</React.Fragment>;
      });
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return null;
    }

    const element = node as HTMLElement;
    const props: Record<string, string> = {};
    Array.from(element.attributes).forEach((attr) => {
      props[attr.name] = attr.value;
    });

    const children = Array.from(element.childNodes).map((child, idx) =>
      toReactNode(child, `${key}-${idx}`)
    );

    return React.createElement(element.tagName.toLowerCase(), { ...props, key }, ...children);
  };

  return Array.from(doc.body.childNodes).map((node, index) =>
    toReactNode(node, `story-node-${index}`)
  );
}

export function ArtistStoryRichText({
  storyHtml,
  hasStory,
  variant,
  className,
  placeholder = STORY_PLACEHOLDER,
  maskExactName,
}: ArtistStoryRichTextProps) {
  const base = variantClasses[variant];

  if (!hasStory || !storyHtml) {
    return (
      <p className={cn(base, WORD_WRAP_ROOT, !hasStory && 'italic', className)}>
        {placeholder}
      </p>
    );
  }

  const shouldMaskExactName = Boolean(maskExactName && maskExactName.trim());

  if (shouldMaskExactName) {
    return (
      <div className={cn(base, 'artist-story-html', WORD_WRAP_ROOT, className)}>
        {htmlToReactNodesWithMaskedName(storyHtml, maskExactName as string)}
      </div>
    );
  }

  return (
    <div
      className={cn(base, 'artist-story-html', WORD_WRAP_ROOT, className)}
      dangerouslySetInnerHTML={{ __html: storyHtml }}
    />
  );
}
